import React, { useState, Children } from 'react';
import { ChevronRightIcon, ChevronDownIcon, BuildingIcon, MapPinIcon, UsersIcon, FolderIcon, MoreVerticalIcon, PlusIcon, EditIcon, EyeIcon, ArchiveIcon } from 'lucide-react';
interface OrgUnit {
  id: string;
  code: string;
  name: string;
  description?: string;
  orgUnitType: {
    name: string;
    code: string;
  };
  path: string;
  depth: number;
  isActive: boolean;
  isOrgRoot: boolean;
  logoUrl?: string | null;
  logoFileId?: string | null;
  childOrgUnits: OrgUnit[];
}
interface OrgUnitTreeProps {
  orgUnits: OrgUnit[];
  searchTerm: string;
  onSelect: (orgUnit: OrgUnit) => void;
  onEdit: (orgUnit: OrgUnit) => void;
  onAddChild: (parentId: string) => void;
  locale: string;
}
export function OrgUnitTree({
  orgUnits,
  searchTerm,
  onSelect,
  onEdit,
  onAddChild,
  locale
}: OrgUnitTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(orgUnits.map(ou => ou.id)));
  const toggleNode = (id: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };
  const getOrgUnitIcon = (typeCode: string) => {
    switch (typeCode) {
      case 'ENTITY':
        return <BuildingIcon className="h-4 w-4 text-indigo-600" data-id="element-19887" />;
      case 'SITE':
        return <MapPinIcon className="h-4 w-4 text-green-600" data-id="element-19888" />;
      case 'DEPT':
        return <UsersIcon className="h-4 w-4 text-blue-600" data-id="element-19889" />;
      default:
        return <FolderIcon className="h-4 w-4 text-gray-600" data-id="element-19890" />;
    }
  };
  const filterOrgUnits = (units: OrgUnit[]): OrgUnit[] => {
    if (!searchTerm) return units;
    return units.map(unit => ({
      ...unit,
      childOrgUnits: filterOrgUnits(unit.childOrgUnits)
    })).filter(unit => unit.name.toLowerCase().includes(searchTerm.toLowerCase()) || unit.code.toLowerCase().includes(searchTerm.toLowerCase()) || unit.childOrgUnits.length > 0);
  };
  const renderOrgUnit = (orgUnit: OrgUnit, level: number = 0) => {
    const isExpanded = expandedNodes.has(orgUnit.id);
    const hasChildren = orgUnit.childOrgUnits.length > 0;
    const indent = level * 24;
    return <div key={orgUnit.id} data-id="element-19891">
        <div className="group flex items-center py-2 px-3 hover:bg-gray-50 rounded-lg transition-colors" style={{
        paddingLeft: `${indent + 12}px`
      }} data-id="element-19892">
          {/* Expand/Collapse Button */}
          <button onClick={() => toggleNode(orgUnit.id)} className="mr-2 p-0.5 hover:bg-gray-200 rounded" disabled={!hasChildren} data-id="element-19893">
            {hasChildren ? isExpanded ? <ChevronDownIcon className="h-4 w-4 text-gray-600" data-id="element-19894" /> : <ChevronRightIcon className="h-4 w-4 text-gray-600" data-id="element-19895" /> : <div className="w-4 h-4" data-id="element-19896" />}
          </button>

          {/* Icon or Logo */}
          <div className="mr-2 flex items-center justify-center" data-id="element-19897">
            {orgUnit.logoUrl ? <img src={orgUnit.logoUrl} alt={`${orgUnit.name} logo`} className="max-h-6 max-w-12 object-contain" data-id="element-19898" /> : getOrgUnitIcon(orgUnit.orgUnitType.code)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-19899">
            <div className="flex items-center" data-id="element-19900">
              <button onClick={() => onSelect(orgUnit)} className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate" data-id="element-19901">
                {orgUnit.name}
              </button>
              <span className="ml-2 text-xs text-gray-500 font-mono" data-id="element-19902">
                {orgUnit.code}
              </span>
              {orgUnit.isOrgRoot && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-19903">
                  Root
                </span>}
              {!orgUnit.isActive && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19904">
                  Inactive
                </span>}
            </div>
            <div className="text-xs text-gray-500 truncate" data-id="element-19905">
              {orgUnit.orgUnitType.name}
              {orgUnit.description && ` • ${orgUnit.description}`}
            </div>
          </div>

          {/* Actions - Always Visible */}
          <div className="ml-2 flex items-center space-x-1" data-id="element-19906">
            <button onClick={() => onEdit(orgUnit)} className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded" title="Edit" data-id="element-19907">
              <EditIcon className="h-4 w-4" data-id="element-19908" />
            </button>
            <button onClick={() => onSelect(orgUnit)} className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded" title="View details" data-id="element-19909">
              <EyeIcon className="h-4 w-4" data-id="element-19910" />
            </button>
            <button onClick={() => onAddChild(orgUnit.id)} className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded" title="Add child" data-id="element-19911">
              <PlusIcon className="h-4 w-4" data-id="element-19912" />
            </button>
          </div>
        </div>

        {/* Children */}
        {isExpanded && hasChildren && <div data-id="element-19913">
            {orgUnit.childOrgUnits.map(child => renderOrgUnit(child, level + 1))}
          </div>}
      </div>;
  };
  const filteredOrgUnits = filterOrgUnits(orgUnits);
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19914">
      <div className="p-4" data-id="element-19915">
        {filteredOrgUnits.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-19916">
            No organisation units found matching your search.
          </div> : <div className="space-y-1" data-id="element-19917">
            {filteredOrgUnits.map(orgUnit => renderOrgUnit(orgUnit))}
          </div>}
      </div>
    </div>;
}